export const DraggableStamp = {
  mounted() {
    console.log("DraggableStamp mounted:", this.el.id);
    this.initDraggable();
  },

  updated() {
    console.log('DraggableStamp updated:', this.el.id);
    // Don't reinitialize during active dragging to prevent conflicts
    if (!this.el.dataset.isDragging) {
      this.initDraggable();
    }
  },

  initDraggable() {
    const stamp = this.el;
    // Find the post container (should have relative positioning)
    const postContainer = stamp.closest('[class*="relative"]');

    if (!stamp || !postContainer) {
      console.error(
        "DraggableStamp: Could not find stamp or post container element",
      );
      return;
    }

    console.log(
      "Initializing draggable for stamp:",
      stamp.id,
      "in post container:",
      postContainer,
    );

    let isDragging = false;
    let startX, startY;
    let initialLeft, initialTop;
    let positionIndicators = null;
    let dragClone = null;
    let isHovering = false;

    // Only enable dragging on hover
    const handleMouseEnter = () => {
      isHovering = true;
      stamp.style.cursor = "move";
    };

    const handleMouseLeave = () => {
      if (!isDragging) {
        isHovering = false;
        stamp.style.cursor = "";
      }
    };

    // Create position indicators overlay
    const createPositionIndicators = () => {
      if (positionIndicators) return positionIndicators;

      const overlay = document.createElement("div");
      overlay.className = "position-indicators-overlay";

      // Create a 3x3 grid of position zones relative to post container
      const positions = [
        { name: "top_left", class: "top-2 left-2" },
        { name: "top", class: "top-2 left-1/2 transform -translate-x-1/2" },
        { name: "top_right", class: "top-2 right-2" },
        {
          name: "center_left",
          class: "top-1/2 left-2 transform -translate-y-1/2",
        },
        {
          name: "center",
          class: "top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
        },
        {
          name: "center_right",
          class: "top-1/2 right-2 transform -translate-y-1/2",
        },
        { name: "bottom_left", class: "bottom-2 left-2" },
        {
          name: "bottom",
          class: "bottom-2 left-1/2 transform -translate-x-1/2",
        },
        { name: "bottom_right", class: "bottom-2 right-2" },
      ];

      positions.forEach((pos) => {
        const indicator = document.createElement("div");
        indicator.className = `position-indicator absolute ${pos.class}`;
        indicator.dataset.position = pos.name;

        // Add label
        const label = document.createElement("span");
        label.className =
          "absolute top-6 left-1/2 transform -translate-x-1/2 text-xs bg-white px-2 py-1 rounded shadow-md opacity-0 transition-opacity";
        label.textContent = pos.name.replace("_", " ");
        indicator.appendChild(label);

        overlay.appendChild(indicator);
      });

      postContainer.appendChild(overlay);
      positionIndicators = overlay;
      return overlay;
    };

    // Remove position indicators
    const removePositionIndicators = () => {
      if (positionIndicators) {
        positionIndicators.remove();
        positionIndicators = null;
      }
    };

    // Define relative position zones based on post container
    const getRelativePositions = () => {
      const containerRect = {
        width: postContainer.offsetWidth,
        height: postContainer.offsetHeight,
      };
      const centerX = containerRect.width / 2;
      const centerY = containerRect.height / 2;

      return {
        top_left: { x: 0, y: 0 },
        top: { x: centerX, y: 0 },
        top_right: { x: containerRect.width, y: 0 },
        center_left: { x: 0, y: centerY },
        center: { x: centerX, y: centerY },
        center_right: { x: containerRect.width, y: centerY },
        bottom_left: { x: 0, y: containerRect.height },
        bottom: { x: centerX, y: containerRect.height },
        bottom_right: { x: containerRect.width, y: containerRect.height },
      };
    };

    // Calculate closest relative position
    const getClosestPosition = (x, y) => {
      const positions = getRelativePositions();
      let closest = "center";
      let minDistance = Infinity;

      Object.entries(positions).forEach(([name, pos]) => {
        const distance = Math.sqrt(
          Math.pow(x - pos.x, 2) + Math.pow(y - pos.y, 2),
        );
        if (distance < minDistance) {
          minDistance = distance;
          closest = name;
        }
      });

      return closest;
    };

    // Highlight closest position indicator
    const highlightClosestIndicator = (closestPosition) => {
      if (!positionIndicators) return;

      // Reset all indicators
      positionIndicators
        .querySelectorAll("[data-position]")
        .forEach((indicator) => {
          indicator.classList.remove("closest");
          const label = indicator.querySelector("span");
          if (label) label.classList.remove("opacity-100");
        });

      // Highlight closest
      const closestIndicator = positionIndicators.querySelector(
        `[data-position="${closestPosition}"]`,
      );
      if (closestIndicator) {
        closestIndicator.classList.add("closest");
        const label = closestIndicator.querySelector("span");
        if (label) label.classList.add("opacity-100");
      }
    };

    // Mouse down event - only works on hover
    const handleMouseDown = (e) => {
      if (e.button !== 0) return; // Only left click
      if (!isHovering) return; // Only allow drag when hovering
      if (isDragging) return; // Prevent multiple drags

      e.preventDefault();
      e.stopPropagation();

      console.log("Starting drag for stamp:", stamp.id);

      isDragging = true;
      startX = e.clientX;
      startY = e.clientY;

      const stampRect = stamp.getBoundingClientRect();
      const postRect = postContainer.getBoundingClientRect();

      // Calculate position relative to post container, not stamp
      initialLeft = stampRect.left - postRect.left;
      initialTop = stampRect.top - postRect.top;

      // Add flag to prevent LiveView updates during drag
      stamp.dataset.isDragging = "true";
      
      // Create a dragging clone to avoid CSS conflicts
      dragClone = stamp.cloneNode(true);
      dragClone.id = stamp.id + '-drag-clone';
      dragClone.style.cssText = `
        position: absolute !important;
        left: ${initialLeft}px !important;
        top: ${initialTop}px !important;
        z-index: 9999 !important;
        pointer-events: none !important;
        opacity: 0.8 !important;
        transform: scale(1.05) !important;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
      `;
      
      // Hide original stamp and show clone
      stamp.style.opacity = '0.3';
      postContainer.appendChild(dragClone);

      // Show position indicators
      try {
        createPositionIndicators();
      } catch (error) {
        console.error("Failed to create position indicators:", error);
      }

      // Add visual feedback to post container
      postContainer.classList.add("ring-2", "ring-violet-400", "ring-opacity-50");

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };

    // Mouse move event
    const handleMouseMove = (e) => {
      if (!isDragging || !dragClone) return;

      e.preventDefault();

      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      const newLeft = initialLeft + deltaX;
      const newTop = initialTop + deltaY;

      // Keep clone within post container bounds
      const containerRect = {
        width: postContainer.offsetWidth,
        height: postContainer.offsetHeight,
      };
      const cloneRect = {
        width: dragClone.offsetWidth,
        height: dragClone.offsetHeight,
      };

      const maxLeft = containerRect.width - cloneRect.width;
      const maxTop = containerRect.height - cloneRect.height;

      const boundedLeft = Math.max(0, Math.min(maxLeft, newLeft));
      const boundedTop = Math.max(0, Math.min(maxTop, newTop));

      // Move the clone
      dragClone.style.left = `${boundedLeft}px`;
      dragClone.style.top = `${boundedTop}px`;

      // Calculate and highlight closest position within post container
      const stampCenterX = boundedLeft + cloneRect.width / 2;
      const stampCenterY = boundedTop + cloneRect.height / 2;
      const closestPosition = getClosestPosition(stampCenterX, stampCenterY);
      highlightClosestIndicator(closestPosition);
    };

    // Mouse up event
    const handleMouseUp = (e) => {
      if (!isDragging || !dragClone) return;

      console.log("Ending drag for stamp:", stamp.id);
      isDragging = false;

      // Calculate center position of clone relative to container
      const cloneRect = {
        left: parseFloat(dragClone.style.left),
        top: parseFloat(dragClone.style.top),
        width: dragClone.offsetWidth,
        height: dragClone.offsetHeight,
      };

      const stampCenterX = cloneRect.left + cloneRect.width / 2;
      const stampCenterY = cloneRect.top + cloneRect.height / 2;

      console.log("Final stamp center position:", {
        x: stampCenterX,
        y: stampCenterY,
      });

      // Get closest relative position
      const closestPosition = getClosestPosition(stampCenterX, stampCenterY);

      // Calculate offsets from the relative position
      const positions = getRelativePositions();
      const targetPos = positions[closestPosition];

      const offsetX = Math.round(stampCenterX - targetPos.x);
      const offsetY = Math.round(stampCenterY - targetPos.y);

      console.log("Snapping to position:", closestPosition, "with offsets:", {
        x: offsetX,
        y: offsetY,
      });

      // Clean up dragging state
      delete stamp.dataset.isDragging;
      stamp.style.opacity = "";

      // Remove drag clone
      if (dragClone && dragClone.parentNode) {
        dragClone.parentNode.removeChild(dragClone);
        dragClone = null;
      }

      // Remove container feedback
      postContainer.classList.remove(
        "ring-2",
        "ring-violet-400",
        "ring-opacity-50",
      );

      // Remove position indicators
      removePositionIndicators();

      // Send update to LiveView
      const stampId = stamp.dataset.stampId || stamp.id.replace("stamp-", "");
      const isMobile = window.innerWidth < 768; // md breakpoint

      const updateData = {
        stamp_id: stampId,
        relative_position: closestPosition,
        relative_position_x_offset: offsetX,
        relative_position_y_offset: offsetY,
      };

      // Also update mobile position if we're in mobile view
      if (isMobile) {
        updateData.mobile_relative_position = closestPosition;
        updateData.mobile_relative_position_x_offset = offsetX;
        updateData.mobile_relative_position_y_offset = offsetY;
      }

      console.log("Updating stamp position:", updateData);

      // Update accessibility label
      stamp.setAttribute(
        "aria-label",
        `Draggable stamp. Current position: ${closestPosition}. Use arrow keys to move, Enter to show position guide.`,
      );
      stamp.dataset.currentPosition = closestPosition;

      // Show success animation
      const showSuccessAnimation = () => {
        stamp.classList.add("ring-4", "ring-green-400", "ring-opacity-75");
        setTimeout(() => {
          stamp.classList.remove("ring-4", "ring-green-400", "ring-opacity-75");
        }, 1000);
      };

      try {
        // Use pushEventTo with the correct target from phx-target attribute
        const target = stamp.getAttribute('phx-target');
        if (target) {
          this.pushEventTo(target, "update_stamp_position", updateData);
        } else {
          this.pushEvent("update_stamp_position", updateData);
        }
        showSuccessAnimation();
      } catch (error) {
        console.error("Failed to push stamp position update:", error);
        // Show error animation
        stamp.classList.add("ring-4", "ring-red-400", "ring-opacity-75");
        setTimeout(() => {
          stamp.classList.remove("ring-4", "ring-red-400", "ring-opacity-75");
        }, 1000);
      }

      // Clean up event listeners
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    // Add touch support for mobile devices
    const handleTouchStart = (e) => {
      if (e.touches.length !== 1) return;

      const touch = e.touches[0];
      const mouseEvent = new MouseEvent("mousedown", {
        clientX: touch.clientX,
        clientY: touch.clientY,
        button: 0,
      });

      handleMouseDown(mouseEvent);
    };

    const handleTouchMove = (e) => {
      if (!isDragging || e.touches.length !== 1) return;

      e.preventDefault();

      const touch = e.touches[0];
      const mouseEvent = new MouseEvent("mousemove", {
        clientX: touch.clientX,
        clientY: touch.clientY,
      });

      handleMouseMove(mouseEvent);
    };

    const handleTouchEnd = (e) => {
      if (!isDragging) return;

      const mouseEvent = new MouseEvent("mouseup", {
        clientX: 0,
        clientY: 0,
      });

      handleMouseUp(mouseEvent);
    };

    // Remove existing event listeners if they exist
    if (this.cleanup) {
      this.cleanup();
    }

    // Add event listeners - only mouse events for hover-based dragging
    stamp.addEventListener("mouseenter", handleMouseEnter);
    stamp.addEventListener("mouseleave", handleMouseLeave);
    stamp.addEventListener("mousedown", handleMouseDown);

    console.log("Mouse event listeners added to stamp:", stamp.id);

    // Store cleanup function
    this.cleanup = () => {
      console.log("Cleaning up event listeners for stamp:", stamp.id);
      stamp.removeEventListener("mouseenter", handleMouseEnter);
      stamp.removeEventListener("mouseleave", handleMouseLeave);
      stamp.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      
      // Clean up drag clone if it exists
      if (dragClone && dragClone.parentNode) {
        dragClone.parentNode.removeChild(dragClone);
        dragClone = null;
      }
      
      // Remove any position indicators
      removePositionIndicators();
    };
  },

  destroyed() {
    if (this.cleanup) {
      this.cleanup();
    }

    // Clean up any remaining position indicators
    if (this.el) {
      const postContainer = this.el.closest('[class*="relative"]');
      if (postContainer) {
        const indicators = postContainer.querySelector(
          ".position-indicators-overlay",
        );
        if (indicators) {
          indicators.remove();
        }
      }
    }
  },
};
